import {LatestCommitProvider} from '@github-ui/code-view-shared/contexts/LatestCommitContext'
import type {
  DataRouterBlamePayload,
  DataRouterBlobLayoutPayload,
  DataRouterBlobPayload,
} from '@github-ui/code-view-types'
import {useChildRouteQuery, useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Outlet, useMatches, useSearchParams} from '@github-ui/react-router'
import {useMemo} from 'react'

import {BlobHeaderActions, CodeViewHeaderDataRouter} from '../components/headers/CodeViewHeader'
import {DeferredASTProvider, useDeferredASTQuery} from '../contexts/DeferredASTContext'
import {DeferredMetadataProvider, EmptyDeferredMetadata} from '../contexts/DeferredMetadataContext'
import {type CurrentBlob, CurrentBlobProvider} from '../hooks/CurrentBlob'
import {CurrentStyledBlobProvider} from '../hooks/CurrentStyledBlob'
import {codeViewBlameRoute} from '../routes/code-view-blame-route'
import {codeViewBlobLayoutRoute} from '../routes/code-view-blob-layout-route'
import {codeViewBlobRoute} from '../routes/code-view-blob-route'

function mergeBlobPayloads(
  layoutData: DataRouterBlobLayoutPayload,
  routeData: DataRouterBlobPayload | DataRouterBlamePayload,
  isPlainSearchParam: boolean,
): CurrentBlob {
  const {headerInfo: layoutHeaderInfo, ...layoutBlob} = layoutData.blob

  // blame doesn't have blob data to merge
  if ('blame' in routeData) {
    return {
      csv: null,
      csvError: null,
      richText: null,
      symbols: {not_analyzed: true, timed_out: false, symbols: []},
      renderedFileInfo: null,
      issueTemplate: null,
      discussionTemplate: undefined,
      ...layoutBlob,
      headerInfo: {
        ...layoutHeaderInfo,
        toc: null,
      },
      isPlain: layoutBlob.isPlain || isPlainSearchParam,
    }
  }

  const {headerInfo: routeHeaderInfo, ...routeBlob} = routeData as DataRouterBlobPayload

  return {
    ...layoutBlob,
    ...routeBlob,
    headerInfo: {
      ...layoutHeaderInfo,
      ...routeHeaderInfo,
    },
    isPlain: layoutBlob.isPlain || isPlainSearchParam,
  }
}

export function CodeViewBlobLayout() {
  const {data: layoutData} = useRouteQuery(codeViewBlobLayoutRoute, 'mainQuery')
  const matches = useMatches()
  const isBlob = !!matches.find(match => match.id === codeViewBlobRoute.id)

  const {data: routeData} = useChildRouteQuery(isBlob ? codeViewBlobRoute : codeViewBlameRoute, 'mainQuery')
  const {data: styledBlobData} = useRouteQuery(codeViewBlobLayoutRoute, 'codeViewBlobLayoutRoute.StyledBlob')
  const {data: deferredMetadataQuery} = useRouteQuery(codeViewBlobLayoutRoute, 'deferredMetadata')
  const {
    data: latestCommit,
    error: latestCommitError,
    isLoading: latestCommitLoading,
  } = useRouteQuery(codeViewBlobLayoutRoute, 'latestCommit')

  const deferredAST = useDeferredASTQuery(
    layoutData.refInfo,
    layoutData.path,
    layoutData.error?.httpStatus === 404,
    styledBlobData?.rawLines?.length ?? 100000,
  )

  const [urlParams] = useSearchParams()
  const isPlainSearchParam = urlParams.get('plain') === '1'

  const blob = useMemo(
    () =>
      routeData && layoutData && !layoutData.error
        ? mergeBlobPayloads(layoutData, routeData, isPlainSearchParam)
        : null,
    [layoutData, routeData, isPlainSearchParam],
  )

  if (layoutData?.error) {
    return <Outlet />
  }

  if (!routeData || !layoutData || !blob) {
    return null
  }

  const deferredMetadata = deferredMetadataQuery ?? EmptyDeferredMetadata

  return (
    <LatestCommitProvider latestCommit={latestCommit} error={latestCommitError} isLoading={latestCommitLoading}>
      <DeferredMetadataProvider {...deferredMetadata}>
        <DeferredASTProvider {...deferredAST}>
          <CurrentBlobProvider blob={blob}>
            <CurrentStyledBlobProvider blob={styledBlobData}>
              <CodeViewHeaderDataRouter
                narrowActions={<BlobHeaderActions payload={layoutData} narrow />}
                wideActions={<BlobHeaderActions payload={layoutData} narrow={false} />}
                path={layoutData.path}
                commitish={layoutData.refInfo.name}
                modelsRepoIntegrationEnabled={layoutData.modelsRepoIntegrationEnabled}
              />
              <Outlet />
            </CurrentStyledBlobProvider>
          </CurrentBlobProvider>
        </DeferredASTProvider>
      </DeferredMetadataProvider>
    </LatestCommitProvider>
  )
}
